/**
 * Instruction Service
 * Handles instruction-related API calls
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { InstructionAdapter } from '@/adapters';
import type {
  ApiResponse,
  Instruction,
  CreateInstructionDto,
  UpdateInstructionDto,
  InstructionResponseDto,
  InstructionsListResponseDto,
} from '@/types';

export const instructionService = {
  /**
   * Get all instructions
   */
  async getInstructions(params?: { bu_id?: string }): Promise<Instruction[]> {
    const response = await api.get<ApiResponse<InstructionsListResponseDto>>(
      API_ENDPOINTS.INSTRUCTIONS.BASE,
      { params }
    );
    return InstructionAdapter.fromApiList(response.data.data.instructions);
  },

  /**
   * Create new instruction
   */
  async createInstruction(data: CreateInstructionDto, buScope: { bu_id?: string } = {}): Promise<Instruction> {
    const payload = InstructionAdapter.toCreateRequest(data);
    const response = await api.post<ApiResponse<InstructionResponseDto>>(
      withBuScope(API_ENDPOINTS.INSTRUCTIONS.BASE, buScope),
      payload
    );
    return InstructionAdapter.fromApi(response.data.data);
  },

  /**
   * Update instruction
   */
  async updateInstruction(id: string, data: UpdateInstructionDto): Promise<Instruction> {
    const payload = InstructionAdapter.toUpdateRequest(data);
    const response = await api.put<ApiResponse<InstructionResponseDto>>(
      API_ENDPOINTS.INSTRUCTIONS.BY_ID(id),
      payload
    );
    return InstructionAdapter.fromApi(response.data.data);
  },

  /**
   * Toggle instruction status
   */
  async toggleStatus(id: string, status: boolean): Promise<Instruction> {
    const payload = InstructionAdapter.toToggleStatusRequest(status);
    const response = await api.patch<ApiResponse<InstructionResponseDto>>(
      API_ENDPOINTS.INSTRUCTIONS.STATUS(id),
      payload
    );
    return InstructionAdapter.fromApi(response.data.data);
  },

  /**
   * Delete instruction
   */
  async deleteInstruction(id: string): Promise<void> {
    await api.delete<ApiResponse<null>>(API_ENDPOINTS.INSTRUCTIONS.BY_ID(id));
  },
};

